import { Injectable } from "@angular/core";
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpResponse,
  HttpErrorResponse
} from "@angular/common/http";
import { Observable } from "rxjs";
import { CookieService } from "ngx-cookie-service";
import { tap } from 'rxjs/operators';
import { RoutePartsService } from "../services/route-parts.service";
import { ActivatedRoute, Router } from "@angular/router";
import { MatSnackBar } from "@angular/material/snack-bar";
import { environment } from "../../../environments/environment";
import { FullLoaderService } from "../services/full-loader.service";
import { AuthSessionStateService } from "../services/auth-session-state.service";
// Author: Akarsh Singh | 2026-09-14 | Clear comparison SWR after cross-screen service writes.
import { ServiceComparisonSWRService } from '../../views/operations/compare-service-supplier-quotes/service-comparison-swr.service';
// Author: Akarsh Singh | 2026-09-14 | Invoice details follow service and tax/bank mutations.
import { ServiceInvoiceDetailsSWRService } from '../../views/operations/service-invoice-details/service-invoice-details-swr.service';

// Author: Akarsh Singh | 2026-09-15 | Catalogue invalidation and safe, actionable failure feedback.
import { ServiceRfqWizardSWRService } from '../services/service-rfq-wizard-swr.service';
import { ServiceCatalogueSWRService } from '../services/service-catalogue-swr.service';
import { FriendlyApiErrorService } from '../services/friendly-api-error.service';
// Author: Akarsh Singh | 2026-09-21 | Invalidate completion snapshots after cross-surface writes and session failures.
import { ServiceCompletionReviewSWRService } from '../../views/operations/buyer-service-completion-review/service-completion-review-swr.service';
import { BuyerServiceInvoiceSWRService } from '../../views/operations/service-invoice-list/buyer-service-invoices-list/buyer-service-invoice-swr.service';
import { BuyerPoSWRService } from '../../views/operations/purchase-order/purchase-order-list/po-swr.service';
import { SupplierScreenSWRService } from '../services/supplier-screen-swr.service';
import { isS3Url, SIGNED_READ_URL_API } from '../services/private-file.service';
@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  /** Add all domains here, where you would not like to interceptor to intercept the API call */
  excludedDomainList: string[] = [
    "ihm-cdn-dev.varuna-sentinels.com",
    "ihm-cdn-prod.varuna-sentinels.com",
  ];

  /** Add all UI routes here, where you would not like to interceptor to intercept the API response */
  excludedResponseRoutesList: string[] = [
    "/sessions/signin2",
    "/sessions/signup2",
    "/sessions/signin",
    "/sessions/signup",
    "/auth/signin",
    "/auth/signup"
  ];

  constructor(
    private cookieService: CookieService,
    private routePartsService: RoutePartsService, 
    private activatedRoute: ActivatedRoute,
    private _fullLoaderService: FullLoaderService,
    private authSession: AuthSessionStateService,
    private comparisonSWR: ServiceComparisonSWRService,
    private invoiceDetailsSWR: ServiceInvoiceDetailsSWRService,
    private snack: MatSnackBar,
    private router: Router,
    private catalogueSWR: ServiceCatalogueSWRService,
    private rfqWizardSWR: ServiceRfqWizardSWRService,
    private completionSWR: ServiceCompletionReviewSWRService,
    private buyerInvoiceSWR: BuyerServiceInvoiceSWRService,
    private buyerPoSWR: BuyerPoSWRService,
    private friendlyErrors: FriendlyApiErrorService,
    private supplierScreenSWR: SupplierScreenSWRService) {
  }

  private isCompletionListMutation(req: HttpRequest<any>): boolean {
    return this.completionSWR.isMutation(req.url, req.method)
      && /\/(submitServiceCompletion|acceptServiceCompletion|createSupplierFeedback|updateSupplierFeedback)(?:\/|$)/.test(req.url.split('?')[0]);
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let exclude: boolean = false;
    this.excludedDomainList.forEach(x => exclude = req.url.includes(x)? true : exclude);
    // 2026-10-25 (S3 step 2): requests to S3 (presigned PUT / GET) never carry the session headers or the API key.
    const s3Request = isS3Url(req.url);
    if (s3Request) exclude = true;

    let changedReq: HttpRequest<any> = req;

    if(!exclude){
      const tokenId = this.cookieService.get("tokenId");
      const tokenHash = this.cookieService.get("tokenHash");
      const _userId = this.cookieService.get("userId");
      let _shipId: string;
  
      // Do not send shipId in request header if we are not in ship screen
      if (this.routePartsService.generateRouteParts(this.activatedRoute.snapshot).length > 0) {
        if (this.routePartsService.generateRouteParts(this.activatedRoute.snapshot)[0].url == 'ship') {
          if (this.cookieService.check("shipId")) {
            _shipId = this.cookieService.get("shipId");
          } else {
            _shipId = "null";
          }
        } else {
          _shipId = "null";
        }
      }
  
      // A request that already carries session headers (the T&C step of sign-in sends the just-issued session before
      // the cookies exist) keeps them; stale cookies must not replace them (2026-10-23).
      if (req.headers.has("id") && req.headers.has("hash")) {
        changedReq = req.clone({ headers: req.headers.set("x-api-key", environment.apiKey) });
      } else if (tokenId && tokenHash) {
        let headers = req.headers.set("id", tokenId)
          .set("hash", tokenHash);
        if (_userId) {
          headers = headers.set("userid", _userId);
        }
        // if (_shipId) {
        //   headers = headers.set("shipid", _shipId);
        // }
        headers = headers.set("x-api-key", environment.apiKey);
        changedReq = req.clone({ headers });
      } else if (tokenId && (tokenHash == null || tokenHash == "undefined" || typeof tokenHash == undefined || tokenHash == "")) {
        let headers = req.headers.set("id", tokenId);
        headers = headers.set("x-api-key", environment.apiKey);
        changedReq = req.clone({ headers });
      } else {
        let headers = req.headers.set("x-api-key", environment.apiKey);
        changedReq = req.clone({ headers });
      }
    }
    
    return next.handle(changedReq).pipe(
      tap(
        event => {
          if (event instanceof HttpResponse) {
            if (this.supplierScreenSWR.isMutation(req.url, req.method)) this.supplierScreenSWR.invalidateAll();
            if (this.completionSWR.isMutation(req.url, req.method)) this.completionSWR.invalidateAll();
            if (this.isCompletionListMutation(req)) this.buyerInvoiceSWR.invalidateAll();
            if (this.buyerPoSWR.isMutation(req.url, req.method)) this.buyerPoSWR.invalidateAll();
            if (this.rfqWizardSWR.isMutation(req.url, req.method)) this.rfqWizardSWR.invalidateAll();
            if (this.catalogueSWR.isMutation(req.url, req.method)) this.catalogueSWR.invalidateAll();
            if (this.invoiceDetailsSWR.isMutation(req.url, req.method)) this.invoiceDetailsSWR.invalidateAll();
            if (this.comparisonSWR.isMutation(req.url, req.method)) this.comparisonSWR.invalidateAll();
          }
        },
        error => {
          if (this.supplierScreenSWR.isMutation(req.url, req.method) || error.status === 401 || error.status === 403) this.supplierScreenSWR.invalidateAll();
          if (this.completionSWR.isMutation(req.url, req.method)) this.completionSWR.invalidateAll();
          if (this.isCompletionListMutation(req)) this.buyerInvoiceSWR.invalidateAll();
          if (this.buyerPoSWR.isMutation(req.url, req.method)) this.buyerPoSWR.invalidateAll();
          if (this.rfqWizardSWR.isMutation(req.url, req.method)) this.rfqWizardSWR.invalidateAll();
          if (this.catalogueSWR.isMutation(req.url, req.method)) this.catalogueSWR.invalidateAll();
          if (this.invoiceDetailsSWR.isMutation(req.url, req.method)) this.invoiceDetailsSWR.invalidateAll();
          if (this.comparisonSWR.isMutation(req.url, req.method)) this.comparisonSWR.invalidateAll();
          this._fullLoaderService.hideLoader();
          if (error instanceof HttpErrorResponse) {
            // Handle error response here
            const currentPath = (this.router.url || '').split('?')[0];
            // 2026-10-25 (S3 step 2): an S3 error (an expired signed URL, a private file) is never a session problem, so
            // it never logs the user out; the signed-read endpoint's 404 is reported by PrivateFileService itself.
            const quietFileRequest = s3Request || req.url.split('?')[0].endsWith(SIGNED_READ_URL_API);
            if (!quietFileRequest && !this.excludedResponseRoutesList.includes(currentPath)) {
              switch (error.status) {
                // Keep adding different error code cases which are common to all requests
                case 401:
                case 403: {
                  this.authSession.markExpired();
                  this.buyerPoSWR.invalidateAll();
                  this.completionSWR.invalidateAll();
                  this.rfqWizardSWR.invalidateAll();
                  this.catalogueSWR.invalidateAll();
                  this.invoiceDetailsSWR.invalidateAll();
                  this.comparisonSWR.invalidateAll();
                  this.cookieService.deleteAll("/");

                  // Do not open multiple snack bars when several in-flight
                  // requests fail after the same session expires.
                  if (!(this.snack._openedSnackBarRef)) {
                    this.snack.open(
                      this.friendlyErrors.describe({ status: 401 }),
                      "OK",
                      { duration: 4000, panelClass: "custom-snack" }
                    );
                  }

                  // Leave the protected layout immediately. Waiting for the
                  // snackbar created a window where other UI (including the
                  // KYC modal) could react to failed API responses.
                  this.router.navigateByUrl('/auth/signin');
                  break;
                }
                default: {
                  // These pages render contextual inline errors and retry controls themselves.
                  const requestPath = req.url.split('?')[0];
                  const reviewRequest = /\/services\/(rfq\/(fetchServiceCompletionReview|sendServiceCompletionClarification|acceptServiceCompletion)|serviceRfq\/(createSupplierFeedback|updateSupplierFeedback))(?:\/|$)/.test(requestPath);
                  if (reviewRequest && currentPath === '/operations/buyer-service-completion-review') break;
                  const comparisonRequest = /\/services\/rfq\/(getCompareServiceSupplierQuotes|createServicePurchaseOrder|generateServiceQuotationAttachment)$/.test(requestPath);
                  if (comparisonRequest && currentPath === '/operations/compare-service-supplier-quotes') break;
                  const catalogueRead = /\/(services\/getServicesList|services\/cart\/(fetch|add|update\/[^/]+)|search\/service\/[^/]+\/service|search\/supplierList\/service\/[^/]+)$/.test(requestPath);
                  if (catalogueRead && /^\/search\/services(?:\/|$)/.test(currentPath)) break;
                  // RFQ wizard pages provide contextual feedback and explicit retry controls.
                  const wizardRequest = /\/services\/rfq\/(getDraftData|getLineItems|getSupplierAssignments|saveHeader|saveLineItems|getServiceSuggestedSuppliers)(?:\/|$)/.test(requestPath);
                  if (wizardRequest && /^\/dashboard\/rfq\/(service-rfq-header|service-create\/(header|line-items))$/.test(currentPath)) break;
                  // "Already on MPS" (409 from a supplier create path): the page shows the Link / create-new choices.
                  if (error.status === 409 && error.error?.code === 'EXISTING_SUPPLIER_MATCHES') break;
                  // Supplier reliability and the KYC badge are optional decoration: a failure shows nothing, never a snackbar.
                  if (/\/supplier\/(reliability|kycBadge)$/.test(requestPath)) break;
                  // The "Already on MPS?" lookup never blocks a screen: its caller falls back to the create path's own check.
                  if (/\/supplier\/(existingSupplierMatches|editableSupplierIds)$/.test(requestPath)) break;
                  // Quote outcome banner (404 = not the caller's quote / nothing to show) and the reject dialog's
                  // reason list (it offers "reject without a reason" on failure) handle errors silently.
                  if (/\/quote\/(fetchQuoteOutcomeForSupplier|fetchQuoteLossReasons)$/.test(requestPath)) break;
                  // 2026-10-24 Batch B: catalogue matches (404 = not invited) and the port coverage prompt / report are
                  // optional helpers on their screens and handle their own errors silently.
                  if (/\/quote\/fetchCatalogueMatchesForRfq$|\/port\/(myPortCoverage|adminPortCoverageReport)$/.test(requestPath)) break;
                  // 2026-10-24 Batch C: the paperwork panels (match, receipt lines, invoice queries, tolerances) show their
                  // own messages in place; the list chips are optional and stay hidden on failure.
                  if (/\/(buyer|supplier|adminDashboard)\/paperwork\//.test(requestPath)) break;
                  if (!(this.snack._openedSnackBarRef)) {
                    this.snack.open(this.friendlyErrors.describe(error), "OK", {
                      duration: 3000,
                      panelClass: "custom-snack",
                    });
                  }
                  break;
                }
              }
            } else {
              //console.log("Token interceptor error response handling skipped due to excluded route.");
            }
          }
        }
      )
    );
  }

}
